// Currencies: types, a `use…()` hook and a picker every module can drop into a form.
//
// Typed into a text box, a currency is a spelling contest — "EUR", "eur", "Euro" — and the first
// report that groups by it splits the euro three ways. The list is short and already agreed, so
// offer it, and say what the chosen one is worth while people are looking.
import { useApi } from "@keel/core";
import { Select } from "../Select";
import { Field } from "../ui";

export interface Currency {
  id: string;
  /** ISO 4217, three upper-case letters. */
  code: string;
  name: string;
  /** Printed in front of amounts when set ("$", "£", "kr"). */
  symbol: string | null;
  /** How many decimals an amount in this currency has: 2 for most, 0 for yen-like ones. */
  decimalPlaces: number;
  isActive: boolean;
  /** EUR: every rate is quoted against it and it is 1 by definition. */
  isBase: boolean;
  /** The newest rate entered — how many EUR one unit buys. `null` until somebody enters one. */
  rate: number | null;
  rateValidFrom: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CurrencyRate {
  id: string;
  currencyId: string;
  /** The first day this rate applies; it stays in force until the next one. */
  validFrom: string;
  /** How many EUR one unit of the currency buys. */
  rate: number;
  source: "manual" | "ecb" | "import";
  createdAt: string;
}

/** The rate in force for a currency on a day (`GET /setup/currencies/rate`). */
export interface CurrencyRateOn {
  code: string;
  on: string;
  rate: number;
  validFrom: string | null;
  source: CurrencyRate["source"] | "base";
  decimalPlaces: number;
}

/**
 * The tenant's currencies, active ones unless asked for everything. Retired currencies are still
 * wanted wherever an old card or document has to show the one it was written in.
 */
export function useCurrencies(includeInactive = false) {
  return useApi<Currency[]>(`/setup/currencies${includeInactive ? "?includeInactive=true" : ""}`);
}

/** "1 USD = 0.92 EUR", or what to do about a currency that has no rate yet. */
export function rateLabel(c: Currency): string {
  if (c.isBase) return "The base currency";
  if (c.rate == null) return `No ${c.code} rate yet — enter one under Settings → Master data → Currencies`;
  return `1 ${c.code} = ${c.rate} EUR`;
}

export interface CurrencySelectProps {
  /** The ISO code, which is what cards and documents store. */
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** Allow no currency at all, with this label. */
  emptyLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * The bare control. Only active currencies are offered, plus whatever is already chosen: a card
 * written in a currency that was retired since must still show it rather than go blank. The same
 * goes for a code the list does not have at all — EUR on a tenant that never opened the list is
 * the common case, since the base needs no row to be valid.
 */
export function CurrencySelect({
  value,
  onChange,
  emptyLabel,
  disabled,
  placeholder = "Choose a currency…",
}: CurrencySelectProps) {
  const currencies = useCurrencies(true).data ?? [];
  const usable = currencies.filter((c) => c.isActive || c.code === value);
  const unlisted = !!value && !currencies.some((c) => c.code === value);
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {unlisted && <option value={value}>{value}</option>}
      {usable.map((c) => (
        <option key={c.code} value={c.code}>
          {c.code} · {c.name}
          {c.isActive ? "" : " (retired)"}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and the chosen currency's rate as the hint. */
export function CurrencyField({
  label,
  hint,
  ...rest
}: CurrencySelectProps & { label: string; hint?: string }) {
  const currencies = useCurrencies(true).data ?? [];
  const chosen = currencies.find((c) => c.code === rest.value);
  return (
    <Field label={label} hint={chosen ? rateLabel(chosen) : hint}>
      <CurrencySelect {...rest} />
    </Field>
  );
}
